import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { socket } from "App";
import {
  setScriptIndexList,
  setSocketData,
} from "store/reducers/marketWatch.slice";
import { emitIndexSocketSubscriptions } from "utils/indexStripRows";
import { processIndexSocketTick } from "utils/marketWatchIndexSocket";
import { getSessionData } from "utils/common";
import { loadTickCache, persistTickCache } from "utils/tickCache";

/**
 * Index live feed — same source + logic as fxbazzar_mobileview MarketWatch.js:
 * socket.io → processIndexSocketTick → Redux scriptIndexList → MarketWatchIndexStrip
 */
export function useMarketWatchIndexSocket({ symbolNameArray = [] } = {}) {
  const dispatch = useDispatch();
  const scriptIndexList = useSelector((s) => s.marketWatch?.scriptIndexList);
  const marketWatchList = useSelector((s) => s.marketWatch?.marketWatchList);

  const scriptIndexListRef = useRef(scriptIndexList);
  const marketWatchListRef = useRef(marketWatchList);
  const tickCacheRef = useRef({});
  const tickCacheFlushRef = useRef(null);
  const userIdRef = useRef("");

  useEffect(() => {
    scriptIndexListRef.current = scriptIndexList;
  }, [scriptIndexList]);

  useEffect(() => {
    marketWatchListRef.current = marketWatchList;
  }, [marketWatchList]);

  useEffect(() => {
    const session = getSessionData() || {};
    const userId =
      session?.userId ??
      session?.id ??
      session?.UserId ??
      session?.UserID ??
      "";
    userIdRef.current = userId;
    tickCacheRef.current = loadTickCache(userId);
    return () => {
      if (tickCacheFlushRef.current) clearTimeout(tickCacheFlushRef.current);
    };
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const onTick = (raw) => {
      const normalized = processIndexSocketTick({
        raw,
        scriptIndexList: scriptIndexListRef.current,
        marketWatchList: marketWatchListRef.current,
        tickCacheRef,
        onIndexMapUpdated: (newIdx) => {
          dispatch(setScriptIndexList(newIdx));
          if (tickCacheFlushRef.current) clearTimeout(tickCacheFlushRef.current);
          tickCacheFlushRef.current = setTimeout(() => {
            tickCacheFlushRef.current = null;
            persistTickCache(userIdRef.current, tickCacheRef.current);
          }, 500);
        },
      });
      if (normalized && Object.keys(normalized).length > 0) {
        dispatch(setSocketData(normalized));
      }
    };

    const onConnect = () => {
      if (symbolNameArray?.length > 0) {
        socket.emit("room", symbolNameArray.toString());
      }
      emitIndexSocketSubscriptions(socket, scriptIndexListRef.current);
    };

    socket.off("message");
    socket.off("tick");
    socket.on("message", onTick);
    socket.on("tick", onTick);
    socket.off("connect", onConnect);
    socket.on("connect", onConnect);

    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off("message", onTick);
      socket.off("tick", onTick);
      socket.off("connect", onConnect);
    };
  }, [dispatch, symbolNameArray]);

  useEffect(() => {
    if (!socket?.connected || !scriptIndexList || scriptIndexList.size === 0) return;
    emitIndexSocketSubscriptions(socket, scriptIndexList);
  }, [scriptIndexList]);
}
